<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="canViewCases; else noPermission">

  <app-page-hero
    eyebrow="Container Lifecycle"
    title="Container Cases"
    subtitle="Primary land-container workspace for lifecycle status, active legs, and linked operational context.">
    <div actions>
      <p-button *ngIf="canCreateCase" label="Booking Intake" icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="createCase()"></p-button>
      <p-button label="Refresh" icon="pi pi-refresh" (click)="loadWorkspace()"></p-button>
    </div>
  </app-page-hero>

  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

  <app-page-search-panel
    title="Filter Cases"
    [activeFilters]="activeFilterCount"
    [resultText]="filteredCases.length + ' cases'"
    (search)="applyFilters()"
    (clear)="clearFilters()">
    <div class="p-fluid grid formgrid">
      <div class="field col-12 md:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="caseNumber" pInputText [(ngModel)]="filters.caseNumber">
            <label for="caseNumber">Case Number</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="containerNumber" pInputText [(ngModel)]="filters.containerNumber">
            <label for="containerNumber">Container</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="billOfLadingNumber" pInputText [(ngModel)]="filters.billOfLadingNumber">
            <label for="billOfLadingNumber">BL</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
          <p-iftaLabel>
          <app-logistics-project-lookup inputId="logisticsProjectId" [(ngModel)]="filters.logisticsProjectId"></app-logistics-project-lookup>
            <label for="logisticsProjectId">Project</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select id="caseStatus" [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="filters.status" [showClear]="true" styleClass="w-full"></p-select>
            <label for="caseStatus">Status</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="currentState" pInputText [(ngModel)]="filters.currentState">
            <label for="currentState">Current State</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-datePicker id="openedFrom" [(ngModel)]="filters.openedFrom" dateFormat="yy-mm-dd" styleClass="w-full"></p-datePicker>
            <label for="openedFrom">Opened From</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-calendar-plus"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-datePicker id="openedTo" [(ngModel)]="filters.openedTo" dateFormat="yy-mm-dd" styleClass="w-full"></p-datePicker>
            <label for="openedTo">Opened To</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
    </div>
  </app-page-search-panel>

  <app-page-data-panel
    [title]="directoryViewLabel()"
    [subtitle]="directoryView === 'CASES'
      ? 'Container identity, commercial context, lifecycle progress, and current operational control in one view.'
      : 'Shared commercial reference groups for coordinating the containers in the same lifecycle intake.'"
    [resultText]="directoryView === 'CASES'
      ? filteredCases.length + ' of ' + cases.length + ' cases'
      : caseReferenceGroups.length + ' reference groups'">
    <div actions class="directory-view-switch" aria-label="Container case directory view">
      <p-button label="Cases" icon="pi pi-list" size="small" [outlined]="directoryView !== 'CASES'"
        (onClick)="setDirectoryView('CASES')"></p-button>
      <p-button label="By Booking" icon="pi pi-bookmark" size="small" [outlined]="directoryView !== 'BOOKING'"
        (onClick)="setDirectoryView('BOOKING')"></p-button>
      <p-button label="By Bill of Lading" icon="pi pi-file" size="small" [outlined]="directoryView !== 'BILL_OF_LADING'"
        (onClick)="setDirectoryView('BILL_OF_LADING')"></p-button>
    </div>

    @if (directoryView === 'CASES') {
      <p-table
      [value]="filteredCases"
      [rows]="10"
      [paginator]="true"
      [rowsPerPageOptions]="[10, 25, 50]"
      [rowHover]="true"
      [resizableColumns]="true"
      dataKey="id"
      styleClass="p-datatable-sm container-case-table"
      [tableStyle]="{ 'min-width': '100rem' }"
      currentPageReportTemplate="Showing {first}-{last} of {totalRecords} container cases"
      [showCurrentPageReport]="true">
      <ng-template pTemplate="header">
        <tr>
          <th class="case-action-col">Actions</th>
          <th style="width: 20rem">Case & Container</th>
          <th style="width: 21rem">Commercial References</th>
          <th style="width: 20rem">Customer & Project</th>
          <th style="width: 23rem">Lifecycle & Latest State</th>
          <th style="width: 21rem">Current Control & Timeline</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-caseItem>
        <tr>
          <td class="case-action-col">
            <div class="case-row-actions">
              <p-button
                icon="pi pi-eye"
                [rounded]="true"
                [text]="true"
                severity="info"
                pTooltip="Open container case"
                tooltipPosition="right"
                (onClick)="openCase(caseItem.id)">
              </p-button>
              <p-button
                *ngIf="caseItem.logisticsProjectId"
                icon="pi pi-sitemap"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                pTooltip="Open linked project"
                tooltipPosition="right"
                (onClick)="openProject(caseItem.logisticsProjectId)">
              </p-button>
            </div>
          </td>

          <td>
            <div class="case-rich-stack">
              <button type="button" class="case-link-button" (click)="openCase(caseItem.id)">
                {{ caseItem.caseNumber || 'Case reference pending' }}
              </button>
              <strong class="container-number"><i class="pi pi-box"></i>{{ caseItem.containerNumber || 'Container pending' }}</strong>
              <div class="case-tag-row">
                <p-tag [value]="caseItem.isoEquipmentType || 'Equipment pending'" severity="secondary"></p-tag>
                <p-tag *ngIf="caseItem.sealNumber" value="Seal recorded" severity="success"></p-tag>
              </div>
              <span><i class="pi pi-lock"></i>{{ caseItem.sealNumber || 'Seal not captured' }}</span>
            </div>
          </td>

          <td>
            <div class="case-reference-grid">
              <div>
                <span>Bill of Lading</span>
                <strong>{{ caseItem.billOfLadingNumber || 'Not captured' }}</strong>
              </div>
              <div>
                <span>Booking</span>
                <strong>{{ caseItem.bookingNumber || 'Not captured' }}</strong>
              </div>
              <div>
                <span>House BL</span>
                <strong>{{ caseItem.houseBillNumber || 'Not captured' }}</strong>
              </div>
              <div>
                <span>Customs Declaration</span>
                <strong>{{ caseItem.customsDeclarationNumber || 'Not captured' }}</strong>
              </div>
            </div>
          </td>

          <td>
            <div class="case-rich-stack">
              <span class="case-cell-label">Customer</span>
              <strong>{{ projectCustomerLabel(caseItem) }}</strong>
              <span class="case-cell-label">Logistics Project</span>
              <button *ngIf="caseItem.logisticsProjectId; else directLifecycle" type="button"
                class="case-link-button compact" (click)="openProject(caseItem.logisticsProjectId)">
                {{ projectLabel(caseItem) }}
              </button>
              <ng-template #directLifecycle>
                <strong>Direct container lifecycle</strong>
              </ng-template>
              <span><i class="pi pi-briefcase"></i>{{ projectReferenceLabel(caseItem) }}</span>
            </div>
          </td>

          <td>
            <div class="case-rich-stack lifecycle-stack">
              <div class="case-tag-row">
                <p-tag [value]="formatLifecycleLabel(caseItem.status || 'OPEN')" [severity]="statusSeverity(caseItem.status)"></p-tag>
                <p-tag [value]="formatLifecycleLabel(caseItem.currentState || 'STATE_PENDING')" severity="info"></p-tag>
              </div>
              <div *ngIf="hasJourneyProgress(caseItem); else journeyPlanPending"
                class="journey-progress"
                [class.on-hold]="caseItem.status === 'ON_HOLD'"
                [class.completed]="journeyProgressPercent(caseItem) === 100">
                <div class="journey-progress-heading">
                  <span>Journey completion</span>
                  <strong>{{ journeyProgressPercent(caseItem) }}%</strong>
                </div>
                <p-progressBar [value]="journeyProgressPercent(caseItem)" [showValue]="false"></p-progressBar>
                <small>{{ lifecycleProgressCaption(caseItem) }}</small>
              </div>
              <ng-template #journeyPlanPending>
                <div class="journey-progress journey-progress-empty">
                  <div class="journey-progress-heading">
                    <span>Journey completion</span>
                    <strong>Plan pending</strong>
                  </div>
                  <small>Define journey steps to measure lifecycle completion.</small>
                </div>
              </ng-template>
              <div class="current-milestone">
                <span>{{ hasJourneyProgress(caseItem) ? 'Current journey step' : 'Current milestone' }}</span>
                <strong>{{ journeyCurrentStepLabel(caseItem) }}</strong>
                <small>{{ lifecycleMeta(caseItem) }}</small>
              </div>
            </div>
          </td>

          <td>
            <div class="case-rich-stack">
              <div class="operation-control">
                <span [class.active]="!!caseItem.activeTransportLegId">
                  <i class="pi pi-truck"></i>
                  {{ caseItem.activeTransportLegId ? 'Transport active' : 'Transport inactive' }}
                </span>
                <span [class.active]="!!caseItem.activeStorageLegId">
                  <i class="pi pi-warehouse"></i>
                  {{ caseItem.activeStorageLegId ? 'Storage active' : 'Storage inactive' }}
                </span>
              </div>
              <strong>{{ currentControlLabel(caseItem) }}</strong>
              <span><i class="pi pi-calendar-plus"></i>Opened {{ caseItem.openedAt ? (caseItem.openedAt | date:'dd MMM yyyy, h:mm a') : 'date pending' }}</span>
              <span><i class="pi pi-clock"></i>Updated {{ caseItem.updatedAt ? (caseItem.updatedAt | date:'dd MMM yyyy, h:mm a') : 'not recorded' }}</span>
              <span *ngIf="caseItem.closedAt"><i class="pi pi-check-circle"></i>Closed {{ caseItem.closedAt | date:'dd MMM yyyy, h:mm a' }}</span>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6">
            <div class="empty-state">
              <i class="pi pi-search"></i>
              <strong>No container cases found</strong>
              <span>No lifecycle cases match the current filter set.</span>
            </div>
          </td>
        </tr>
      </ng-template>
      </p-table>
    } @else {
      <div class="case-reference-groups">
        @for (group of caseReferenceGroups; track group.key) {
          <section class="case-reference-group">
            <header class="reference-group-header">
              <div class="reference-group-title">
                <span class="reference-group-eyebrow">{{ directoryView === 'BOOKING' ? 'Booking Number' : 'Bill of Lading' }}</span>
                <strong>{{ group.reference }}</strong>
                <small>Latest case opened {{ group.latestOpenedAt ? (group.latestOpenedAt | date:'dd MMM yyyy, h:mm a') : 'not recorded' }}</small>
              </div>
              <div class="reference-group-control">
                <div class="reference-group-metrics">
                  <span><strong>{{ group.cases.length }}</strong> containers</span>
                  <span [class.warn]="group.activeCaseCount > 0"><strong>{{ group.activeCaseCount }}</strong> active</span>
                  <span [class.danger]="group.holdCaseCount > 0"><strong>{{ group.holdCaseCount }}</strong> on hold</span>
                  <span><strong>{{ group.projectCount }}</strong> projects</span>
                </div>
                <p-button *ngIf="hasReferenceWorkspace(group)"
                  [label]="directoryView === 'BOOKING' ? 'Open Booking Workspace' : 'Open BL Workspace'"
                  icon="pi pi-briefcase" severity="secondary" size="small" [outlined]="true"
                  (onClick)="openReferenceWorkspace(group)"></p-button>
              </div>
            </header>
            <p-table [value]="group.cases" dataKey="id" styleClass="p-datatable-sm container-case-group-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>Case & Container</th>
                  <th>Lifecycle</th>
                  <th>Project Context</th>
                  <th>Current Control</th>
                  <th class="group-action-col">Actions</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-caseItem>
                <tr>
                  <td>
                    <div class="case-rich-stack">
                      <button type="button" class="case-link-button" (click)="openCase(caseItem.id)">{{ caseItem.caseNumber || 'Case reference pending' }}</button>
                      <strong class="container-number"><i class="pi pi-box"></i>{{ caseItem.containerNumber || 'Container pending' }}</strong>
                      <span><i class="pi pi-tag"></i>{{ caseItem.isoEquipmentType || 'Equipment pending' }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="case-rich-stack">
                      <div class="case-tag-row">
                        <p-tag [value]="formatLifecycleLabel(caseItem.status || 'OPEN')" [severity]="statusSeverity(caseItem.status)"></p-tag>
                        <p-tag [value]="formatLifecycleLabel(caseItem.currentState || 'STATE_PENDING')" severity="info"></p-tag>
                      </div>
                      <div *ngIf="hasJourneyProgress(caseItem); else groupJourneyPending" class="compact-journey-progress">
                        <div>
                          <span>Journey completion</span>
                          <strong>{{ journeyProgressPercent(caseItem) }}%</strong>
                        </div>
                        <p-progressBar [value]="journeyProgressPercent(caseItem)" [showValue]="false"></p-progressBar>
                        <small>{{ lifecycleProgressCaption(caseItem) }}</small>
                      </div>
                      <ng-template #groupJourneyPending><span>Journey plan pending</span></ng-template>
                    </div>
                  </td>
                  <td>
                    <div class="case-rich-stack">
                      <strong>{{ projectCustomerLabel(caseItem) }}</strong>
                      <button *ngIf="caseItem.logisticsProjectId; else directGroupLifecycle" type="button" class="case-link-button compact" (click)="openProject(caseItem.logisticsProjectId)">{{ projectLabel(caseItem) }}</button>
                      <ng-template #directGroupLifecycle><span>Direct container lifecycle</span></ng-template>
                      <span><i class="pi pi-briefcase"></i>{{ projectReferenceLabel(caseItem) }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="case-rich-stack">
                      <div class="operation-control">
                        <span [class.active]="!!caseItem.activeTransportLegId"><i class="pi pi-truck"></i>{{ caseItem.activeTransportLegId ? 'Transport active' : 'Transport inactive' }}</span>
                        <span [class.active]="!!caseItem.activeStorageLegId"><i class="pi pi-warehouse"></i>{{ caseItem.activeStorageLegId ? 'Storage active' : 'Storage inactive' }}</span>
                      </div>
                      <span>{{ currentControlLabel(caseItem) }}</span>
                    </div>
                  </td>
                  <td class="group-action-col">
                    <p-button icon="pi pi-eye" [rounded]="true" [text]="true" severity="info" pTooltip="Open container case" tooltipPosition="left" (onClick)="openCase(caseItem.id)"></p-button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        } @empty {
          <div class="empty-state">
            <i class="pi pi-search"></i>
            <strong>No reference groups found</strong>
            <span>No container cases match the current filter set.</span>
          </div>
        }
      </div>
    }
  </app-page-data-panel>
</app-page-shell>

<ng-template #noPermission>
  <app-page-shell>
    <app-page-data-panel title="Container Cases" subtitle="You do not have permission to view container lifecycle cases."></app-page-data-panel>
  </app-page-shell>
</ng-template>
